import React, { useState } from 'react'
import { X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface ApiErrorDetails {
  equipmentId?: string
  approvalLevel?: string
  estimatedCost?: number
  [key: string]: string | number | undefined
}

interface FormattedError {
  message: string
  details?: ApiErrorDetails
  code?: string
}

interface ApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  onApprove: (comments?: string) => Promise<void>
  requestId: string
  equipmentDescription?: string
  problemDescription?: string
  estimatedCost?: number
  urgency?: string
}

export default function ApprovalModal({
  isOpen,
  onClose,
  onApprove,
  requestId,
  equipmentDescription,
  problemDescription,
  estimatedCost,
  urgency
}: ApprovalModalProps) {
  const [comments, setComments] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FormattedError | null>(null)

  const formatErrorMessage = (err: unknown): FormattedError => {
    // Tratamento para objetos de erro planos (não instância de Error)
    if (err && typeof err === 'object') {
      const apiError = err as Record<string, unknown>
      // Prioriza campo 'error' do backend, depois 'message', depois fallback
      const errorMessage = (typeof apiError.error === 'string' ? apiError.error : null)
        || (typeof apiError.message === 'string' ? apiError.message : null)
        || 'Erro ao aprovar requisição'
      return {
        message: errorMessage,
        details: apiError.details as ApiErrorDetails | undefined,
        code: typeof apiError.code === 'string' ? apiError.code : undefined
      }
    }
    
    // Tratamento para instâncias de Error
    if (err instanceof Error) {
      return {
        message: err.message,
        details: undefined,
        code: undefined
      }
    }
    
    return { message: 'Erro ao aprovar requisição' }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      setError(null)
      
      // ✅ Validação de estimatedCost ANTES de aprovar (defesa em profundidade)
      if (!estimatedCost || estimatedCost <= 0) {
        setError({
          message: 'Não é possível aprovar: Custo estimado inválido ou ausente',
          details: {
            estimatedCost,
            message: 'Por favor, atualize a requisição com um custo estimado válido antes de aprovar'
          }
        });
        setLoading(false);
        return;
      }
      
      await onApprove(comments.trim() || undefined)
      
      toast.success('Requisição aprovada com sucesso!', {
        duration: 3000,
        position: 'top-right',
      })
      
      // Resetar formulário
      setComments('')
      
      onClose()
    } catch (err) {
      setError(formatErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setComments('')
      setError(null)
      onClose()
    }
  }

  const getUrgencyBadgeClass = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'bg-gray-100 text-gray-800'
      case 'media':
        return 'bg-yellow-100 text-yellow-800'
      case 'alta':
        return 'bg-orange-100 text-orange-800'
      case 'critica':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getUrgencyLabel = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'Baixa'
      case 'media':
        return 'Média'
      case 'alta':
        return 'Alta'
      case 'critica':
        return 'Crítica'
      default:
        return urgency || 'N/A'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div data-testid="approval-modal" className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-brand-primary-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                Aprovar Requisição
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Informações da solicitação */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-1">
                {equipmentDescription || `Solicitação #${requestId?.substring(0, 8)}...`}
              </p>
              {problemDescription && (
                <p className="text-sm text-brand-text-secondary mb-2">
                  {problemDescription.length > 100 
                    ? `${problemDescription.substring(0, 100)}...` 
                    : problemDescription}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs text-brand-text-muted">
                {estimatedCost && (
                  <span>
                    Custo: <span className="font-medium text-brand-text-secondary">€{estimatedCost.toFixed(2)}</span>
                  </span>
                )}
                {urgency && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyBadgeClass(urgency)}`}>
                    {getUrgencyLabel(urgency)}
                  </span>
                )}
              </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Comentários (opcional) */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Comentários de Aprovação (opcional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="Adicione comentários sobre esta aprovação..."
                  data-testid="approval-comments"
                  className="w-full rounded-md border-brand-neutral-300 shadow-sm focus:border-brand-primary-500 focus:ring-brand-primary-500"
                  disabled={loading}
                />
              </div>

              {/* Erro */}
              {error && (
                <div className="flex flex-col gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error.message}</p>
                  </div>
                  {error.details && (
                    <div className="ml-6 text-xs text-red-600 space-y-1">
                      {error.details.equipmentId && (
                        <div>Equipamento: <span className="font-medium">{error.details.equipmentId}</span></div>
                      )}
                      {error.details.approvalLevel && (
                        <div>Nível necessário: <span className="font-medium">{error.details.approvalLevel}</span></div>
                      )}
                      {error.details.estimatedCost !== undefined && error.details.estimatedCost !== null && (
                        <div>Custo estimado: <span className="font-medium">€{error.details.estimatedCost.toFixed(2)}</span></div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  data-testid="approval-cancel-btn"
                  className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="approval-confirm-btn"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-primary-500 border border-transparent rounded-md hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Aprovar...
                    </div>
                  ) : (
                    'Confirmar Aprovação'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}